import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

const PlanHistoryChart = ({ data = [] }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    useEffect(() => {
        // Initialize chart
        if (chartRef.current) {
            chartInstance.current = echarts.init(chartRef.current);
        }

        // Cleanup on unmount
        return () => {
            if (chartInstance.current) {
                chartInstance.current.dispose();
            }
        };
    }, []);

    useEffect(() => {
        if (!chartInstance.current || !data) return;

        // Process data for the chart
        const activeCount = data.filter(plan => plan.status === 'active').length;
        const expiredCount = data.filter(plan => plan.status === 'expired').length;

        const activeRevenue = data
            .filter(plan => plan.status === 'active')
            .reduce((sum, plan) => sum + (plan.p_price || 0), 0);

        const expiredRevenue = data
            .filter(plan => plan.status === 'expired')
            .reduce((sum, plan) => sum + (plan.p_price || 0), 0);

        const totalRevenue = activeRevenue + expiredRevenue;

        const option = {
            title: {
                text: 'Plan History Overview',
                left: 'center',
                top: 10,
                textStyle: {
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#1f2937'
                }
            },
            tooltip: {
                trigger: 'item',
                formatter: (params) => {
                    const revenue = params.name === 'Active' ? activeRevenue : expiredRevenue;
                    return `${params.name}<br/>
                            Count: ${params.value}<br/>
                            Revenue: ₹${revenue}<br/>
                            Percentage: ${params.percent}%`;
                }
            },
            legend: {
                orient: 'horizontal',
                bottom: 10,
                data: ['Active', 'Expired']
            },
            series: [
                {
                    name: 'Plan Status',
                    type: 'pie',
                    radius: ['40%', '70%'],
                    center: ['50%', '50%'],
                    avoidLabelOverlap: true,
                    itemStyle: {
                        borderRadius: 10,
                        borderColor: '#fff',
                        borderWidth: 2
                    },
                    label: {
                        show: true,
                        formatter: '{b}\n{c} ({d}%)',
                        fontSize: 12
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: 14,
                            fontWeight: 'bold'
                        },
                        itemStyle: {
                            shadowBlur: 10,
                            shadowOffsetX: 0,
                            shadowColor: 'rgba(0, 0, 0, 0.5)'
                        }
                    },
                    labelLine: {
                        show: true
                    },
                    data: [
                        {
                            value: activeCount,
                            name: 'Active',
                            itemStyle: {
                                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                    { offset: 0, color: '#10b981' },
                                    { offset: 1, color: '#059669' }
                                ])
                            }
                        },
                        {
                            value: expiredCount,
                            name: 'Expired',
                            itemStyle: {
                                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                    { offset: 0, color: '#ef4444' },
                                    { offset: 1, color: '#dc2626' }
                                ])
                            }
                        }
                    ]
                }
            ],
            graphic: [
                {
                    type: 'text',
                    left: 'center',
                    top: '45%',
                    style: {
                        text: `₹${totalRevenue}`,
                        fontSize: 20,
                        fontWeight: 'bold',
                        fill: '#1f2937',
                        textAlign: 'center'
                    }
                },
                {
                    type: 'text',
                    left: 'center',
                    top: '52%',
                    style: {
                        text: 'Total Revenue',
                        fontSize: 12,
                        fill: '#6b7280',
                        textAlign: 'center'
                    }
                }
            ]
        };

        chartInstance.current.setOption(option);

        // Handle resize
        const handleResize = () => {
            if (chartInstance.current) {
                chartInstance.current.resize();
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [data]);

    // Show empty state if no data
    if (!data || data.length === 0) {
        return (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Plan History Overview</h3>
                <div className="flex items-center justify-center h-64 text-gray-500">
                    <div className="text-center">
                        <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                        </svg>
                        <p className="text-sm">No plan history data available</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mt-4">
            <div ref={chartRef} style={{ width: '100%', height: '350px' }} />

            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
                <div className="text-center">
                    <p className="text-2xl font-bold text-gray-800">{data.length}</p>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Total Plans</p>
                </div>
                <div className="text-center">
                    <p className="text-2xl font-bold text-emerald-600">
                        {data.filter(p => p.status === 'active').length}
                    </p>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Active</p>
                </div>
                <div className="text-center">
                    <p className="text-2xl font-bold text-red-500">
                        {data.filter(p => p.status === 'expired').length}
                    </p>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Expired</p>
                </div>
            </div>
        </div>
    );
};

export default PlanHistoryChart;